// Vehicles at the table (issue 1533; docs/MRCS/shadowrun-3.md §9 step 19): only where the table's system names vehicles
// (so a d20 table never sees either part).
//
// - `BringOutForm`, behind "Bring out a vehicle…" in the dice bag: the rigger (a token of theirs, or any for the DM), a
//   vehicle of the system's vehicle family (read by the server) and what it is called. The server reads the entry again
//   and puts the vehicle's token beside the rigger with the entry's numbers and its own condition monitor.
// - `VehicleTestForm`, behind "Maneuver test…": the vehicle, its driver (or none: a drone on its own Pilot), the test,
//   the driver's skill (the sheet's row, or a rating typed), whether the driver is jacked in through a control rig, pool
//   dice when the driver is in the turn order, the autonav, off the road, the terrain and typed situations. The browser
//   names that and nothing the server decides: the Handling, the rig's level and the TN are the server's.
import { useEffect, useState } from 'react';
import { VEHICLE_LIMITS, addsDice, drawsFor, poolCap, type SeenPool } from '@dragons-table/vtt-shared';
import { selection } from '../../board/selection';
import { fetchTests, fetchVehicles, type TestsAnswer, type VehiclesAnswer } from '../../entries';
import { asksFor } from './asksFor';
import { useTableState } from '../../table/TableState';
import { useShell } from '../context';
import { BTN } from '../ui';

const btn = BTN;
const field = 'rounded bg-stone-800 px-1 py-0.5 text-xs';

interface TokenLike { id: string; kind: string; name?: string; mine?: boolean; character?: string | null }
interface Fighting { tokens: string[]; pools?: Record<string, SeenPool> }

const TYPED = 'typed';
const NONE = '';
const int = (v: string): number | null => (v.trim() !== '' && Number.isInteger(Number(v)) ? Number(v) : null);

function useMine(isDm: boolean) {
  const { store } = useTableState();
  const tokens = Object.values(store.state.objects as Record<string, TokenLike>).filter((o) => o.kind === 'token');
  return { tokens, mine: tokens.filter((o) => (isDm ? true : o.mine)) };
}

export function BringOutForm({ isDm, priv, onClose }: { isDm: boolean; priv: boolean; onClose: () => void }) {
  const { act, setNotice, systemRules } = useTableState();
  const { campaignId } = useShell();
  const view = systemRules.vehicles;
  const { mine } = useMine(isDm);
  const picked = [...selection.get()].find((id) => mine.some((o) => o.id === id));
  const [who, setWho] = useState(picked ?? mine[0]?.id ?? '');
  const [list, setList] = useState<VehiclesAnswer | 'loading'>('loading');
  const [which, setWhich] = useState('0');
  const [called, setCalled] = useState('');
  useEffect(() => {
    let live = true;
    void fetchVehicles(campaignId).then((a) => { if (live) setList(a); });
    return () => { live = false; };
  }, [campaignId]);
  if (!view) return null;
  const rows = list && list !== 'loading' ? list.vehicles : [];
  const chosen = rows[Number(which)] ?? null;
  const ready = Boolean(who && chosen);
  return (
    <form className="space-y-1 rounded border border-sky-800 p-2 text-xs" data-testid="vehicle-place-form" onSubmit={(e) => {
      e.preventDefault();
      if (!ready || !chosen) return;
      const body = {
        kind: 'vehicle.place', rigger: who, vehicle: { name: chosen.name, ref: chosen.ref, stats: chosen.stats },
        ...(called.trim() ? { called: called.trim().slice(0, VEHICLE_LIMITS.name) } : {}), private: isDm ? priv : false,
      };
      act(body as never, (reply) => { if (!reply.ok) setNotice(reply.reason ?? 'That vehicle could not be brought out.'); else onClose(); });
    }}>
      {!mine.length && <p className="text-stone-400">You have no token at the table to bring a vehicle out beside.</p>}
      <div className="flex flex-wrap items-center gap-1">
        <label className="flex items-center gap-1">Rigger
          <select aria-label="Rigger" className={field} value={who} onChange={(e) => setWho(e.target.value)}>
            {mine.map((o) => <option key={o.id} value={o.id}>{o.name || 'Unnamed token'}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-1">brings out
          <select aria-label="Vehicle to bring out" className={field} value={which} onChange={(e) => setWhich(e.target.value)}>
            {rows.map((v, i) => <option key={v.ref} value={String(i)}>{v.name}</option>)}
          </select>
        </label>
        <input aria-label="What it is called" placeholder="Called" maxLength={VEHICLE_LIMITS.name} className={`${field} w-24`} value={called} onChange={(e) => setCalled(e.target.value)} />
      </div>
      {list === 'loading' && <p className="text-stone-400">Reading the vehicles…</p>}
      {list === null && <p className="text-amber-300">The vehicles can't be read just now.</p>}
      {list && list !== 'loading' && list.notice && <p className="text-amber-300">{list.notice}</p>}
      {chosen && <p data-testid="vehicle-place-summary">{chosen.name}: Handling {chosen.stats.handling ?? '—'}, Body {chosen.stats.body ?? '—'}, Armor {chosen.stats.armor ?? '—'}{chosen.stats.pilot !== undefined ? `, Pilot ${chosen.stats.pilot}` : ''} · its own condition monitor</p>}
      <div className="flex gap-1">
        <button type="submit" className={`${btn} border-sky-700`} disabled={!ready}>Bring out</button>
        <button type="button" className={btn} onClick={onClose}>Close</button>
        {isDm && <span className="self-center text-stone-400">{priv ? 'Private: it arrives hidden from the players.' : 'Public: the players see it arrive beside the rigger.'}</span>}
      </div>
    </form>
  );
}

export function VehicleTestForm({ isDm, priv, onClose }: { isDm: boolean; priv: boolean; onClose: () => void }) {
  const { store, act, setNotice, systemRules, scene } = useTableState();
  const { campaignId } = useShell();
  const view = systemRules.vehicles;
  const { tokens, mine } = useMine(isDm);
  const picked = [...selection.get()].find((id) => mine.some((o) => o.id === id));
  const [vehicle, setVehicle] = useState(picked ?? mine[0]?.id ?? '');
  const [driver, setDriver] = useState(NONE);
  const [test, setTest] = useState(view?.tests[0]?.key ?? '');
  const [answer, setAnswer] = useState<TestsAnswer | 'loading'>(null);
  const [skill, setSkill] = useState(TYPED);
  const [typedSkill, setTypedSkill] = useState('');
  const [typedRating, setTypedRating] = useState('4');
  const [rigged, setRigged] = useState(false);
  const [typedRig, setTypedRig] = useState('');
  const [poolDice, setPoolDice] = useState('0');
  const [autonav, setAutonav] = useState(false);
  const [offroad, setOffroad] = useState(false);
  const [terrain, setTerrain] = useState(NONE);
  const [mods, setMods] = useState([{ label: '', value: '0' }, { label: '', value: '0' }]);

  const pilot = tokens.find((o) => o.id === driver);
  useEffect(() => {
    setAnswer(null); setSkill(TYPED);
    if (!pilot?.character) return undefined;
    let live = true;
    setAnswer('loading');
    void fetchTests(campaignId, driver, scene?.id ?? null).then((a) => {
      if (!live) return;
      setAnswer(a);
      if ((a?.choices ?? []).some((c) => c.kind === 'skill' || c.kind === 'specialization')) setSkill('0');
    });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driver, pilot?.character, campaignId]);

  if (!view) return null;
  if (!mine.length) {
    return (
      <div className="rounded border border-stone-700 p-2 text-xs text-stone-400" data-testid="vehicle-test-form">
        You have no vehicle at the table.
        <button type="button" className={`${btn} ml-2`} onClick={onClose}>Close</button>
      </div>
    );
  }
  const got = answer && answer !== 'loading' ? answer : null;
  const skills = (got?.choices ?? []).filter((c) => c.kind === 'skill' || c.kind === 'specialization');
  const chosenSkill = skill === TYPED ? null : skills[Number(skill)] ?? null;
  const rating = driver === NONE ? null : chosenSkill ? chosenSkill.rating : int(typedRating);
  const skillName = chosenSkill ? chosenSkill.name : typedSkill.trim();
  const chosenTest = view.tests.find((t) => t.key === test);
  // Pool dice from the system's vehicle pool, when the driver is jacked in and in the turn order: at most the skill.
  const enc = store.state.encounter as { combatants?: Fighting[] } | undefined;
  const fighting = driver === NONE ? undefined : enc?.combatants?.find((c) => c.tokens.includes(driver));
  const pool = rigged ? (systemRules.pools ?? []).find((p) => p.key === view.pool && addsDice(p) && drawsFor(p, 'vehicle') && fighting?.pools?.[p.key]) ?? null : null;
  const left = pool ? fighting!.pools![pool.key]!.left : 0;
  const cap = pool && rating ? poolCap(pool, 'vehicle', rating) : null;
  const most = Math.min(left, cap ?? left);
  const poolN = pool ? int(poolDice) ?? 0 : 0;
  const ready = Boolean(vehicle && chosenTest && (driver === NONE || (rating && rating >= 1 && skillName)) && poolN >= 0 && poolN <= most);
  const term = (l: string, v: number) => (l.trim() && v ? [{ label: l.trim().slice(0, VEHICLE_LIMITS.label), value: Math.max(-VEHICLE_LIMITS.modifier, Math.min(VEHICLE_LIMITS.modifier, v)) }] : []);
  const rigN = int(typedRig);
  return (
    <form className="space-y-1 rounded border border-sky-800 p-2 text-xs" data-testid="vehicle-test-form" onSubmit={(e) => {
      e.preventDefault();
      if (!ready) return;
      const body = {
        kind: 'vehicle.test', vehicle, ...(driver !== NONE ? { driver, skill: { name: skillName.slice(0, VEHICLE_LIMITS.name), rating: rating!, ...(chosenSkill?.ref ? { ref: chosenSkill.ref } : {}) } } : {}),
        test, rigged: driver !== NONE && rigged, ...(driver !== NONE && rigged && rigN !== null && rigN >= 1 ? { rig: Math.min(VEHICLE_LIMITS.rig, rigN) } : {}),
        ...(pool && poolN > 0 ? { pool: { key: pool.key, dice: poolN } } : {}),
        autonav: Boolean(chosenTest?.autonav) && autonav, offroad: view.offroad && offroad, ...(terrain !== NONE ? { terrain } : {}),
        modifiers: mods.flatMap((x) => term(x.label, int(x.value) ?? 0)), private: isDm ? priv : false,
      };
      act(body as never, (reply) => { if (!reply.ok) setNotice(reply.reason ?? 'That vehicle test could not be made.'); else onClose(); });
    }}>
      <div className="flex flex-wrap items-center gap-1">
        <label className="flex items-center gap-1">Vehicle
          <select aria-label="Vehicle" className={field} value={vehicle} onChange={(e) => setVehicle(e.target.value)}>
            {mine.map((o) => <option key={o.id} value={o.id}>{o.name || 'Unnamed token'}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-1">driven by
          <select aria-label="Driver" className={field} value={driver} onChange={(e) => setDriver(e.target.value)}>
            {view.pilot && <option value={NONE}>Nobody: its own Pilot</option>}
            {mine.filter((o) => o.id !== vehicle).map((o) => <option key={o.id} value={o.id}>{o.name || 'Unnamed token'}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-1">makes a
          <select aria-label="Vehicle test" className={field} value={test} onChange={(e) => setTest(e.target.value)}>
            {view.tests.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
        </label>
      </div>
      {answer === 'loading' && <p className="text-stone-400">Reading the character's sheet…</p>}
      {got?.notice && <p className="text-amber-300">{got.notice}</p>}
      {driver !== NONE && (
        <div className="flex flex-wrap items-center gap-1">
          <label className="flex items-center gap-1">Skill
            <select aria-label="Driver's skill" className={field} value={skill} onChange={(e) => setSkill(e.target.value)}>
              {skills.map((c, i) => <option key={i} value={String(i)}>{c.name} {c.rating}</option>)}
              <option value={TYPED}>A rating typed in…</option>
            </select>
          </label>
          {skill === TYPED && (
            <>
              <input aria-label="Driver's skill name" placeholder="Car" maxLength={VEHICLE_LIMITS.name} className={`${field} w-20`} value={typedSkill} onChange={(e) => setTypedSkill(e.target.value)} />
              <input aria-label="Driver's skill rating" type="number" min={1} max={VEHICLE_LIMITS.rating} className={`${field} w-12`} value={typedRating} onChange={(e) => setTypedRating(e.target.value)} />
            </>
          )}
          <label className="flex items-center gap-1" title="Jacked in through a control rig, in a vehicle adapted for it: the rig's level comes off the TN, and only then may the pool's dice join.">
            <input type="checkbox" aria-label="Jacked in through the control rig" checked={rigged} onChange={(e) => setRigged(e.target.checked)} /> Rigged
          </label>
          {rigged && asksFor(pilot?.character, answer, 'vehicle.rig') && (
            <label className="flex items-center gap-1" title="Only when the driver's sheet has none.">{view.rig}
              <input aria-label="Control rig level" type="number" min={1} max={VEHICLE_LIMITS.rig} className={`${field} w-12`} value={typedRig} onChange={(e) => setTypedRig(e.target.value)} />
            </label>
          )}
          {pool && (
            <label className="flex items-center gap-1" data-testid="vehicle-pool">{pool.label}
              <input aria-label="Pool dice for the vehicle test" type="number" min={0} max={most} className={`${field} w-12`} value={poolDice} onChange={(e) => setPoolDice(e.target.value)} />
              <span className="text-stone-400">of {left}</span>
            </label>
          )}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-1">
        {chosenTest?.autonav && (
          <label className="flex items-center gap-1" title={chosenTest.autonav === 'dice' ? 'Its rating joins the dice.' : 'Its rating joins the TN: it slows a vehicle in a fight.'}>
            <input type="checkbox" aria-label="Autonav on" checked={autonav} onChange={(e) => setAutonav(e.target.checked)} /> Autonav on
          </label>
        )}
        {view.offroad && (
          <label className="flex items-center gap-1">
            <input type="checkbox" aria-label="Off the road" checked={offroad} onChange={(e) => setOffroad(e.target.checked)} /> Off the road
          </label>
        )}
        {view.terrains.length > 0 && (
          <select aria-label="Terrain" className={field} value={terrain} onChange={(e) => setTerrain(e.target.value)}>
            <option value={NONE}>No terrain named</option>
            {view.terrains.map((x) => <option key={x.key} value={x.key}>{x.label} terrain</option>)}
          </select>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-1" title="The situation's TN modifiers: the Maneuver Scores, speed, weather, a large vehicle.">
        <span className="text-stone-400">TN</span>
        {mods.map((x, i) => (
          <span key={i} className="flex items-center gap-1">
            <input aria-label={`Situation ${i + 1} label`} placeholder="situation" maxLength={VEHICLE_LIMITS.label} className={`${field} w-24`} value={x.label} onChange={(e) => setMods(mods.map((y, k) => (k === i ? { ...y, label: e.target.value } : y)))} />
            <input aria-label={`Situation ${i + 1} modifier to the TN`} type="number" className={`${field} w-12`} value={x.value} onChange={(e) => setMods(mods.map((y, k) => (k === i ? { ...y, value: e.target.value } : y)))} />
          </span>
        ))}
      </div>
      {chosenTest && <p data-testid="vehicle-summary">{chosenTest.label} · {driver === NONE ? 'its own Pilot' : `${rating ?? '?'}${poolN ? ` + ${poolN}` : ''} dice`} against the vehicle's {offroad ? 'off-road ' : ''}Handling{rigged && driver !== NONE ? `, less the ${view.rig.toLowerCase()}` : ''}</p>}
      <div className="flex gap-1">
        <button type="submit" className={`${btn} border-sky-700`} disabled={!ready}>Test</button>
        <button type="button" className={btn} onClick={onClose}>Close</button>
        {isDm && <span className="self-center text-stone-400">{priv ? 'Private: the players see nothing of it.' : 'Public: the players see the test.'}</span>}
      </div>
    </form>
  );
}
